import {
  apiClient,
  REFRESH_TOKEN_EXPIRES_AT_NAME,
  REFRESH_TOKEN_NAME,
  TOKEN_EXPIRES_AT_NAME,
  TOKEN_NAME,
  API_URL_PREFIX as DEFAULT_API_PREFIX,
} from "./client"
import {
  getFirebaseAuth,
  isFirebaseEnabled,
  loginWithFirebasePopup,
} from "@/lib/firebase-auth.ts"

const API_URL_PREFIX = "/api/TokenAuth"

export const authApi = {
  authenticate: ({
    userNameOrEmailAddress,
    password,
  }: {
    userNameOrEmailAddress: string
    password: string
  }) => {
    return apiClient.post<any[]>(`${API_URL_PREFIX}/Authenticate`, {
      userNameOrEmailAddress,
      password,
      rememberClient: true,
    })
  },
  getAuthenticationSource: (email: string) => {
    const encodedEmail = encodeURIComponent(email)
    return apiClient.get<any[]>(
      `${API_URL_PREFIX}/GetAuthenticationSource?usernameOrEmail=${encodedEmail}`
    )
  },
  loginWithFirebaseEmailPassword: async ({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<void> => {
    if (!isFirebaseEnabled()) {
      new Error(
        "Firebase is not configured. Set VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID."
      )
      return
    }

    try {
      const auth = getFirebaseAuth()
      if (!auth) {
        new Error("Firebase auth could not be initialized.")
        return
      }

      const { signInWithEmailAndPassword } = await import("firebase/auth")
      const credential = await signInWithEmailAndPassword(auth, email, password)
      const idToken = await credential.user.getIdToken()

      const authData = await apiClient.post<any[]>(
        `${API_URL_PREFIX}/FirebaseAuthenticate`,
        {
          FirebaseIdToken: idToken,
        }
      )
      const now = Date.now()
      localStorage.setItem(TOKEN_NAME, authData.result?.accessToken)
      localStorage.setItem(REFRESH_TOKEN_NAME, authData.result?.refreshToken)
      localStorage.setItem(
        TOKEN_EXPIRES_AT_NAME,
        (now + authData.result?.expireInSeconds * 1000).toString()
      )
      localStorage.setItem(
        REFRESH_TOKEN_EXPIRES_AT_NAME,
        (now + authData.result?.refreshTokenExpireInSeconds * 1000).toString()
      )
    } catch (err) {
      throw new Error(`Request failed with status ${err}`)
    }
  },
  loginWithGoogle: async (): Promise<void> => {
    if (!isFirebaseEnabled()) {
      new Error(
        "Firebase is not configured. Set VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID."
      )
      return
    }

    try {
      const auth = getFirebaseAuth()
      if (!auth) {
        new Error("Firebase auth could not be initialized.")
        return
      }

      const idToken = await loginWithFirebasePopup()
      if (idToken) {
        const authData = await apiClient.post<any[]>(
          `${API_URL_PREFIX}/FirebaseAuthenticate`,
          {
            FirebaseIdToken: idToken,
          }
        )
        const now = Date.now()
        localStorage.setItem(TOKEN_NAME, authData.result?.accessToken)
        localStorage.setItem(REFRESH_TOKEN_NAME, authData.result?.refreshToken)
        localStorage.setItem(
          TOKEN_EXPIRES_AT_NAME,
          (now + authData.result?.expireInSeconds * 1000).toString()
        )
        localStorage.setItem(
          REFRESH_TOKEN_EXPIRES_AT_NAME,
          (now + authData.result?.refreshTokenExpireInSeconds * 1000).toString()
        )
      } else {
        throw new Error(`Cannot login with Google`)
      }
    } catch (err) {
      throw new Error(`Request failed with status ${err}`)
    }
  },
  logout: () => {
    return apiClient.get<any[]>(`${API_URL_PREFIX}/LogOut`)
  },
  verifyEmail: (c) => {
    return apiClient.post<any[]>(
      `${DEFAULT_API_PREFIX}/Account/ActivateEmail`,
      {
        c,
      }
    )
  },
  sendResetPassword: (emailAddress: string) => {
    return apiClient.post<any[]>(
      `${DEFAULT_API_PREFIX}/Account/SendPasswordResetCode`,
      {
        emailAddress,
      }
    )
  },
  resetPassword: ({ c, password }: { c: string; password: string }) => {
    return apiClient.post<any[]>(
      `${DEFAULT_API_PREFIX}/Account/ResetPassword`,
      {
        c,
        password,
      }
    )
  },
}
